// Cart AJAX re-renders this section without overlay-group settings, so only
// swap line items and totals. Leave header/footer chrome (recently viewed,
// discount, etc.) as the page originally rendered it.
const CART_DIALOG_MS = 440;
window.BaseElementMixin = window.BaseElementMixin || function (Base) {
  return class extends Base {
    connectedCallback() {}
    on(target, type, handler, options = {}) {
      target.addEventListener(type, handler, options);
      return handler;
    }
  };
};

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function openCartDialog(dialog, { instant = false } = {}) {
  if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;

  dialog.classList.remove('is-open', 'is-closing');

  if (instant || prefersReducedMotion()) {
    dialog.classList.add('is-open', 'is-restored');
    dialog.showModal();
    return;
  }

  dialog.classList.remove('is-restored');
  dialog.showModal();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => dialog.classList.add('is-open'));
  });
}

function closeCartDialog(dialog) {
  return new Promise((resolve) => {
    if (!dialog?.open) {
      resolve();
      return;
    }

    const finish = () => {
      dialog.classList.remove('is-open', 'is-closing');
      if (dialog.open) dialog.close();
      resolve();
    };

    if (prefersReducedMotion()) {
      finish();
      return;
    }

    if (dialog.classList.contains('is-closing')) {
      dialog.addEventListener('close', () => resolve(), { once: true });
      return;
    }

    dialog.classList.add('is-closing');
    dialog.classList.remove('is-open');

    let done = false;
    const complete = () => {
      if (done) return;
      done = true;
      dialog.removeEventListener('transitionend', onEnd);
      finish();
    };
    const onEnd = (event) => {
      if (event.target !== dialog) return;
      if (event.propertyName !== 'opacity' && event.propertyName !== 'transform') return;
      complete();
    };

    dialog.addEventListener('transitionend', onEnd);
    setTimeout(complete, CART_DIALOG_MS + 80);
  });
}

function hydrateCartSwatches(cartItems) {
  const swatches = {
    ochre: cartItems?.dataset.ochreSwatch,
    bone: cartItems?.dataset.boneSwatch,
    carbon: cartItems?.dataset.carbonSwatch
  };

  cartItems?.querySelectorAll('.element-cart-item__option').forEach((option) => {
    if (option.querySelector('.element-cart-item__swatch')) return;
    const value = option.querySelector('span:last-child')?.textContent.trim().toLowerCase();
    const imageUrl = swatches[value];
    if (!imageUrl) return;

    const swatch = document.createElement('span');
    swatch.className = 'element-cart-item__swatch';
    swatch.setAttribute('aria-hidden', 'true');
    swatch.style.setProperty('--cart-swatch-image', `url("${imageUrl}")`);
    option.prepend(swatch);
  });
}

function prefersCartMotionReduce() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function waitForCartTransition(element, timeout = 420) {
  return new Promise((resolve) => {
    if (!element) {
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      element.removeEventListener('transitionend', onEnd);
      resolve();
    };
    const onEnd = (event) => {
      if (event.target === element) finish();
    };

    element.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, timeout);
  });
}

function animateCartRowsLeave(rows) {
  if (!rows.length) return Promise.resolve();

  rows.forEach((row) => {
    row.style.height = `${row.offsetHeight}px`;
  });
  rows[0].getBoundingClientRect();
  rows.forEach((row) => {
    row.classList.add('is-leaving');
    row.style.height = '0px';
  });

  return Promise.all(rows.map((row) => waitForCartTransition(row)));
}

function animateCartClear(liveMini) {
  const filled = liveMini.querySelector('cart-items')?.closest('.drawer__scrollable');
  const footer = liveMini.querySelector('.drawer__footer');
  filled?.classList.add('is-clearing');
  footer?.classList.add('is-clearing');

  return Promise.all([
    waitForCartTransition(filled),
    waitForCartTransition(footer)
  ]);
}

function revealCartEmpty(emptyNode) {
  if (!emptyNode) return;
  emptyNode.classList.remove('hidden');
  emptyNode.classList.remove('is-entering');
  emptyNode.offsetHeight;
  emptyNode.classList.add('is-entering');
  emptyNode.addEventListener('animationend', () => {
    emptyNode.classList.remove('is-entering');
  }, { once: true });
}

function revealCartFilled(filledNode, footerNode) {
  [filledNode, footerNode].forEach((node) => {
    if (!node) return;
    node.classList.remove('hidden', 'is-clearing', 'is-entering');
    node.offsetHeight;
    node.classList.add('is-entering');
    node.addEventListener('animationend', () => {
      node.classList.remove('is-entering');
    }, { once: true });
  });
}

let cartSyncToken = 0;

async function syncMiniCartContents(liveMini, nextMini, cart, options = {}) {
  if (!liveMini || !nextMini) return false;

  const count = cart?.item_count ?? nextMini.querySelector('[data-portwell-cart-count]')?.dataset.portwellCartCount;
  if (count != null) document.querySelectorAll('[data-cv-cart-count]').forEach(el => { el.textContent = count; });
  const token = ++cartSyncToken;
  const instant = options.instant || prefersCartMotionReduce();
  const liveItems = liveMini.querySelector('cart-items');
  const nextItems = nextMini.querySelector('cart-items');
  const isEmpty = cart ? cart.item_count === 0 : !nextMini.querySelector('cart-items .horizontal-product');

  if (!instant) {
    const liveRows = Array.from(liveMini.querySelectorAll('cart-items .horizontal-product'));
    const nextIds = new Set(
      Array.from(nextMini.querySelectorAll('cart-items .horizontal-product')).map((row) => row.id)
    );
    const leavingRows = liveRows.filter((row) => row.id && !nextIds.has(row.id));

    if (isEmpty) {
      await animateCartClear(liveMini);
    } else if (leavingRows.length) {
      await animateCartRowsLeave(leavingRows);
    }

    if (token !== cartSyncToken) return false;
  }

  if (liveItems && nextItems) {
    liveItems.innerHTML = nextItems.innerHTML;
    hydrateCartSwatches(liveItems);
  }

  const liveTotal = liveMini.querySelector('.totals__subtotal-value');
  const nextTotal = nextMini.querySelector('.totals__subtotal-value');
  if (liveTotal && nextTotal) {
    liveTotal.innerHTML = nextTotal.innerHTML;
  }

  const livePaymentEstimate = liveMini.querySelector('[data-payment-estimate]');
  const nextPaymentEstimate = nextMini.querySelector('[data-payment-estimate]');
  if (livePaymentEstimate && nextPaymentEstimate) {
    livePaymentEstimate.textContent = nextPaymentEstimate.textContent;
  }

  const livePaymentEstimateDetail = liveMini.querySelector('[data-payment-estimate-detail]');
  const nextPaymentEstimateDetail = nextMini.querySelector('[data-payment-estimate-detail]');
  if (livePaymentEstimateDetail && nextPaymentEstimateDetail) {
    livePaymentEstimateDetail.textContent = nextPaymentEstimateDetail.textContent;
  }

  const liveWarrantyList = liveMini.querySelector('[data-warranty-upgrades]');
  const nextWarrantyList = nextMini.querySelector('[data-warranty-upgrades]');
  if (liveWarrantyList && nextWarrantyList) {
    const openWarrantyDialogId =
      liveWarrantyList.querySelector('[data-cart-info-dialog][open]')?.id;
    const liveUpgrades = liveWarrantyList.querySelectorAll('cart-warranty-upgrade');
    const nextUpgrades = nextWarrantyList.querySelectorAll('cart-warranty-upgrade');
    if (nextUpgrades.length > 0 || liveUpgrades.length === 0) {
      liveWarrantyList.hidden = nextWarrantyList.hidden;
      liveWarrantyList.innerHTML = nextWarrantyList.innerHTML;
      const refreshedWarrantyDialog = Array.from(
        liveWarrantyList.querySelectorAll('[data-cart-info-dialog]')
      ).find((dialog) => dialog.id === openWarrantyDialogId);
      if (refreshedWarrantyDialog && typeof refreshedWarrantyDialog.showModal === 'function') {
        openCartDialog(refreshedWarrantyDialog, { instant: true });
        refreshedWarrantyDialog.focus({ preventScroll: true });
      }
    }
    if (cart) {
      liveWarrantyList.querySelectorAll('cart-warranty-upgrade').forEach((upgrade) => upgrade.syncFromCart(cart));
    }
  }

  const liveFilled = liveMini.querySelector('cart-items')?.closest('.drawer__scrollable');
  const liveFooter = liveMini.querySelector('.drawer__footer');
  const liveEmpty = liveMini.querySelector('.drawer__empty')?.closest('.drawer__scrollable');
  const liveEmptyAtc = liveMini.querySelector('[data-cart-empty-atc]');
  const emptyNow = cart ? cart.item_count === 0 : !nextMini.querySelector('cart-items .horizontal-product');

  if (emptyNow) {
    liveFilled?.classList.add('hidden');
    liveFilled?.classList.remove('is-clearing');
    liveFooter?.classList.add('hidden');
    liveFooter?.classList.remove('is-clearing');
    liveEmptyAtc?.classList.remove('hidden');
    if (!liveEmpty) {
      const nextEmpty = nextMini.querySelector('.drawer__empty')?.closest('.drawer__scrollable');
      const mount = liveMini.querySelector('.flex.flex-col.h-full') || liveMini;
      if (nextEmpty) {
        const emptyNode = nextEmpty.cloneNode(true);
        mount.prepend(emptyNode);
        if (!instant) revealCartEmpty(emptyNode);
      }
    } else if (!instant) {
      revealCartEmpty(liveEmpty);
    } else {
      liveEmpty.classList.remove('hidden');
    }
  } else {
    liveEmpty?.classList.add('hidden');
    liveEmpty?.classList.remove('is-entering');
    liveEmptyAtc?.classList.add('hidden');
    if (!instant) {
      revealCartFilled(liveFilled, liveFooter);
    } else {
      liveFilled?.classList.remove('hidden', 'is-clearing', 'is-entering');
      liveFooter?.classList.remove('hidden', 'is-clearing', 'is-entering');
    }
  }

  return true;
}

function bindTabList(list) {
  if (!list || list.dataset.tabsBound === 'true') return list;

  const controls = () => Array.from(list.querySelectorAll('[aria-controls]'));
  list.addEventListener('click', (event) => {
    const target = event.target.closest('[aria-controls]');
    if (!target || !list.contains(target)) return;
    event.preventDefault();
    controls().forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      document.getElementById(button.getAttribute('aria-controls'))?.removeAttribute('open');
    });
    target.setAttribute('aria-expanded', 'true');
    document.getElementById(target.getAttribute('aria-controls'))?.setAttribute('open', '');
  });
  list.reset = () => {
    const firstControl = controls()[0];
    firstControl?.dispatchEvent(new Event('click', { bubbles: true }));
  };
  list.dataset.tabsBound = 'true';
  return list;
}

if (!customElements.get('tab-list')) {
  customElements.define(
    'tab-list',
    class TabList extends window.BaseElementMixin(HTMLUListElement) {
      connectedCallback() {
        super.connectedCallback();
        bindTabList(this);
      }

      get controls() {
        return this._controls = this._controls || Array.from(this.querySelectorAll('[aria-controls]'));
      }

      reset() {
        bindTabList(this).reset();
      }
    }, { extends: 'ul' }
  );
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends BaseElement {
      constructor() {
        super();

        this.onPrepareBundledSectionsListener = this.onPrepareBundledSections.bind(this);
        this.onCartRefreshListener = this.onCartRefresh.bind(this);
        this.drag = null;
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get open() {
        return this.classList.contains('is-open');
      }

      get panel() {
        return this.querySelector('[data-cart-handle]')?.parentElement || this.querySelector('.element-cart__panel');
      }

      get handle() {
        return this.querySelector('[data-cart-handle]');
      }

      get recentlyViewed() {
        return this.querySelector('recently-viewed');
      }

      get tabList() {
        return bindTabList(this.querySelector('[data-tab-list], [is="tab-list"]'));
      }

      get focusElement() {
        return this.querySelector('[data-cart-close]') || this;
      }

      connectedCallback() {
        super.connectedCallback();

        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        this.on(document, 'cart:bundled-sections', this.onPrepareBundledSectionsListener);
        this.on(document, 'cart:refresh', this.onCartRefreshListener);
        this.on(this, 'submit', this.onCheckoutSubmit.bind(this));
        this.on(this, 'change', this.onTermsChange.bind(this));
        this.on(this, 'click', this.onTermsDialogClick.bind(this));
        this.on(this, 'click', this.onCartInfoDialogClick.bind(this));
        this.on(this, 'cancel', this.onCartDialogCancel.bind(this));
        this.on(this, 'click', this.onCloseClick.bind(this));
        this.on(document, 'keyup', this.onEscape.bind(this));
        this.bindEmptyCartProduct();
        this.bindDrag();
        if (this.recentlyViewed) {
          this.on(this.recentlyViewed, 'is-empty', this.onRecentlyViewedEmpty.bind(this));
        }
      }

      bindEmptyCartProduct() {
        this.on(this, 'click', this.onEmptyCartClick.bind(this));
        this.on(this, 'submit', this.onEmptyCartSubmit.bind(this));
        this.on(document, 'element:sticky-colour-change', this.onEmptyCartStickyColour.bind(this));
        this.on(document, 'element:cart-added', this.onEmptyCartAdded.bind(this));
        if (window.ElementColourState) {
          this.onEmptyCartStickyColour({ detail: window.ElementColourState });
        }
      }

      onEmptyCartAdded() {
        const empty = this.querySelector('.drawer__empty')?.closest('.drawer__scrollable');
        const emptyAtc = this.querySelector('[data-cart-empty-atc]');
        empty?.classList.add('hidden');
        empty?.classList.remove('is-entering');
        emptyAtc?.classList.add('hidden');
      }

      emptyCartColourHandle(colour) {
        const key = String(colour || '').toLowerCase().trim().replace(/\s+/g, '-');
        if (key === 'birch') return 'bone';
        if (key === 'charcoal') return 'carbon';
        return key;
      }

      applyEmptyCartColour(option, { broadcast = true } = {}) {
        if (!option) return;

        const colour = option.dataset.colour;
        const finish = option.dataset.finish || this.emptyCartColourHandle(colour);
        const variantId = option.dataset.variantId;
        const variantPrice = option.dataset.variantPrice;
        const available = option.dataset.variantAvailable === 'true';
        const variantInput = this.querySelector('[data-empty-cart-variant-id]');
        const estimatedDispatchInput = this.querySelector('[data-estimated-dispatch-property]');
        const colourLabel = this.querySelector('[data-empty-cart-colour]');
        const price = this.querySelector('[data-empty-cart-price]');
        const addButton = this.querySelector('[data-empty-cart-add]');
        const addLabel = addButton?.querySelector('[data-empty-cart-add-label]');

        if (variantInput) variantInput.value = variantId;
        if (estimatedDispatchInput) estimatedDispatchInput.value = option.dataset.estimatedDispatch || '';
        if (colourLabel) colourLabel.textContent = colour;
        if (price) price.textContent = variantPrice;
        if (addButton) {
          addButton.disabled = !available;
          if (addLabel) addLabel.textContent = available ? 'Add to your travel bag' : 'Sold Out';
        }

        this.querySelectorAll('[data-empty-cart-image]').forEach((image) => {
          image.hidden = false;
          image.classList.toggle('is-active', image.dataset.finish === finish);
        });

        this.querySelectorAll('[data-empty-cart-option]').forEach((item) => {
          const selected = item.dataset.variantId === variantId;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-selected', String(selected));
        });

        if (broadcast) {
          window.ElementColourState = { colour, finish };
          document.dispatchEvent(new CustomEvent('element:sticky-colour-change', {
            detail: { colour, finish, source: 'empty-cart' }
          }));
        }
      }

      onEmptyCartClick(event) {
        const option = event.target.closest('[data-empty-cart-option]');
        if (option && this.contains(option)) {
          event.preventDefault();
          this.applyEmptyCartColour(option);
        }
      }

      onEmptyCartStickyColour(event) {
        if (event.detail?.source === 'empty-cart') return;
        const colour = event.detail?.colour;
        const finish = event.detail?.finish;
        const option = Array.from(this.querySelectorAll('[data-empty-cart-option]')).find((item) => (
          this.emptyCartColourHandle(item.dataset.colour) === this.emptyCartColourHandle(finish) ||
          this.emptyCartColourHandle(item.dataset.colour) === this.emptyCartColourHandle(colour)
        ));
        if (!option || option.classList.contains('is-selected')) return;
        this.applyEmptyCartColour(option, { broadcast: false });
      }

      async onEmptyCartSubmit(event) {
        const form = event.target.closest('[data-cart-empty-atc] form');
        if (!form || !this.contains(form)) return;

        event.preventDefault();
        event.stopPropagation();

        const addButton = form.querySelector('[data-empty-cart-add]');
        const variantId = form.querySelector('[data-empty-cart-variant-id]')?.value;
        if (!variantId || addButton?.disabled || this.emptyCartAdding) return;

        this.emptyCartAdding = true;
        addButton?.classList.add('is-loading');
        addButton?.setAttribute('aria-busy', 'true');
        if (addButton) addButton.disabled = true;

        try {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');
          const estimatedDispatch = form.querySelector('[data-estimated-dispatch-property]')?.value;
          if (estimatedDispatch) {
            formData.append('properties[Estimated dispatch]', estimatedDispatch);
          }

          const response = await fetch(`${theme.routes.cart_add_url}`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData
          });
          const result = await response.json();
          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Unable to add this product to the cart.');
          }

          document.dispatchEvent(new CustomEvent('element:cart-added', {
            bubbles: true,
            detail: { variantId }
          }));
          document.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open: true, animate: true }
          }));
        } catch (error) {
          console.error(error);
        } finally {
          this.emptyCartAdding = false;
          addButton?.classList.remove('is-loading');
          addButton?.removeAttribute('aria-busy');
          if (addButton) addButton.disabled = false;
        }
      }

      bindDrag() {
        const handle = this.handle;
        if (!handle) return;

        this.on(handle, 'pointerdown', (event) => {
          if (!this.open || event.button || event.pointerType === 'touch') return;
          if (event.target.closest('[data-cart-close]')) return;
          event.preventDefault();
          this.drag = { startY: event.clientY, pointerId: event.pointerId, dragging: false };
          handle.setPointerCapture?.(event.pointerId);
        });

        this.on(handle, 'pointermove', (event) => {
          if (!this.drag || event.pointerId !== this.drag.pointerId || !this.panel) return;
          const delta = event.clientY - this.drag.startY;
          if (!this.drag.dragging && Math.abs(delta) < 6) return;
          this.drag.dragging = true;
          this.classList.add('is-dragging');
          if (window.matchMedia('(max-width: 639px)').matches) {
            this.panel.style.transform = delta > 0 ? `translate3d(0, ${delta}px, 0)` : 'translate3d(0, 0, 0)';
          }
        });

        const endDrag = (event) => {
          if (!this.drag || event.pointerId !== this.drag.pointerId) return;
          const delta = event.clientY - this.drag.startY;
          const shouldClose = this.drag.dragging && delta > Math.min(140, window.innerHeight * 0.18);
          this.drag = null;
          this.classList.remove('is-dragging');
          if (this.panel) this.panel.style.transform = '';
          if (shouldClose) this.hide();
        };

        // Let cart content scroll while it has room; otherwise move the sheet.
        const canScroll = (target, delta) => {
          for (let node = target; node && node !== this; node = node.parentElement) {
            if (!/(auto|scroll)/.test(getComputedStyle(node).overflowY)) continue;
            if (node.scrollHeight <= node.clientHeight + 1) continue;
            if (delta > 0 && node.scrollTop > 0) return true;
            if (delta < 0 && node.scrollTop + node.clientHeight < node.scrollHeight - 1) return true;
          }
          return false;
        };
        let touchDrag = null;
        let suppressClickUntil = 0;
        this.on(this, 'touchstart', (event) => {
          if (!this.open || event.touches.length !== 1 || !window.matchMedia('(max-width: 639px)').matches) return;
          const touch = event.touches[0];
          touchDrag = { x: touch.clientX, y: touch.clientY, lastY: touch.clientY, target: event.target, moving: false, delta: 0 };
        }, { passive: true });
        this.on(this, 'touchmove', (event) => {
          if (!touchDrag || event.touches.length !== 1 || !this.open) return;
          const touch = event.touches[0];
          const delta = touch.clientY - touchDrag.y;
          const step = touch.clientY - touchDrag.lastY;
          touchDrag.lastY = touch.clientY;
          if (!touchDrag.moving) {
            if (Math.abs(delta) < 6 || Math.abs(touch.clientX - touchDrag.x) > Math.abs(delta)) return;
            if (canScroll(touchDrag.target, step)) { touchDrag.y = touch.clientY; return; }
          }
          event.preventDefault();
          // Modal dialogs and the backdrop stay still, but cannot scroll the page.
          if (!touchDrag.target.closest('.element-cart__panel') || touchDrag.target.closest('dialog')) return;
          touchDrag.moving = true;
          touchDrag.delta = delta;
          this.classList.add('is-dragging');
          this.panel.style.transform = `translate3d(0, ${Math.max(0, delta)}px, 0)`;
        }, { passive: false });
        const finishTouch = (event) => {
          if (!touchDrag) return;
          const close = event.type !== 'touchcancel' && touchDrag.moving && touchDrag.delta > Math.min(140, window.innerHeight * 0.18);
          if (touchDrag.moving) suppressClickUntil = Date.now() + 350;
          touchDrag = null;
          this.classList.remove('is-dragging');
          if (this.panel) this.panel.style.transform = '';
          if (close) this.hide();
        };
        this.on(this, 'touchend', finishTouch);
        this.on(this, 'touchcancel', finishTouch);
        this.on(this, 'click', (event) => {
          if (Date.now() < suppressClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
        }, { capture: true });
        this.on(this, 'wheel', (event) => {
          if (this.open && !canScroll(event.target, -event.deltaY)) event.preventDefault();
        }, { passive: false });

        this.on(handle, 'pointerup', endDrag);
        this.on(handle, 'pointercancel', endDrag);
      }

      onCloseClick(event) {
        const onPanel = event.target.closest('.element-cart__panel');
        const closeControl = event.target.closest('[data-cart-close]');
        if (onPanel && !closeControl) return;
        if (event.target === this || closeControl) {
          event.preventDefault();
          this.hide();
        }
      }

      onEscape(event) {
        if (event.code === 'Escape' && this.open) this.hide();
      }

      onCheckoutSubmit(event) {
        const form = event.target.closest('.element-cart-checkout__form');
        if (!form) return;

        const checkbox = form.querySelector('[data-required-terms]');
        if (!checkbox || checkbox.checked) return;

        event.preventDefault();
        const dialog = form.querySelector('[data-terms-dialog]');

        checkbox.setAttribute('aria-invalid', 'true');
        if (dialog && !dialog.open) {
          if (typeof dialog.showModal === 'function') {
            openCartDialog(dialog);
            dialog.focus({ preventScroll: true });
          } else {
            dialog.setAttribute('open', '');
          }
        }
      }

      onTermsChange(event) {
        const checkbox = event.target.closest('[data-required-terms]');
        if (!checkbox || !checkbox.checked) return;

        const form = checkbox.closest('.element-cart-checkout__form');
        const dialog = form?.querySelector('[data-terms-dialog]');

        checkbox.removeAttribute('aria-invalid');
        if (dialog?.open) closeCartDialog(dialog);
      }

      onCartDialogCancel(event) {
        const dialog = event.target.closest('[data-cart-info-dialog], [data-terms-dialog]');
        if (!dialog) return;
        event.preventDefault();
        closeCartDialog(dialog);
      }

      onTermsDialogClick(event) {
        const acceptButton = event.target.closest('[data-accept-terms-and-checkout]');
        const dismissButton = event.target.closest('[data-dismiss-terms-dialog]');
        const dialog = event.target.closest('[data-terms-dialog]');
        if (!dialog || (!acceptButton && !dismissButton && event.target !== dialog)) return;

        const form = dialog.closest('.element-cart-checkout__form');
        const checkbox = form?.querySelector('[data-required-terms]');

        if (acceptButton && form && checkbox) {
          const checkoutButton = form.querySelector('[name="checkout"]');
          checkbox.checked = true;
          checkbox.removeAttribute('aria-invalid');
          closeCartDialog(dialog).then(() => {
            checkoutButton ? form.requestSubmit(checkoutButton) : form.requestSubmit();
          });
          return;
        }

        closeCartDialog(dialog);

        if (!checkbox) return;
        if (event.detail === 0) {
          checkbox.focus({ preventScroll: true });
        } else {
          checkbox.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }

      onCartInfoDialogClick(event) {
        const trigger = event.target.closest('[data-cart-info-trigger]');
        if (trigger) {
          const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
          if (dialog && !dialog.open && typeof dialog.showModal === 'function') {
            openCartDialog(dialog);
            dialog.focus({ preventScroll: true });
          }
          return;
        }

        const dialog = event.target.closest('[data-cart-info-dialog]');
        const dismissButton = event.target.closest('[data-dismiss-cart-info]');
        if (!dialog || (!dismissButton && event.target !== dialog)) return;

        const triggerForDialog = Array.from(
          this.querySelectorAll('[data-cart-info-trigger]')
        ).find((item) => item.getAttribute('aria-controls') === dialog.id);

        closeCartDialog(dialog);
        if (event.detail === 0) triggerForDialog?.focus({ preventScroll: true });
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }

      onRecentlyViewedEmpty() {
        this.recentlyViewed.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <p class="drawer__empty-text h2 heading leading-none tracking-tight">${theme.strings.recentlyViewedEmpty}</p>
          </div>
        </div>
        `;
      }

      async onCartRefresh(event) {
        const id = `MiniCart-${this.sectionId}`;
        if (document.getElementById(id) === null) return;

        this.refreshAbortController?.abort();
        this.refreshAbortController = new AbortController();

        let responseText;
        try {
          responseText = await (await fetch(`${theme.routes.root_url}?section_id=${this.sectionId}`, { signal: this.refreshAbortController.signal })).text();
        }
        catch (error) {
          if (error.name !== 'AbortError') console.error(error);
          return;
        }
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        const source = parsedHTML.getElementById(id);
        const liveMini = document.getElementById(id);
        if (source && liveMini) {
          await syncMiniCartContents(liveMini, source, undefined, {
            instant: event.detail?.animate !== true
          });
        }

        if (event.detail.open === true) {
          this.show();
        }
      }

      show(focusElement = null) {
        if (this.open) {
          this.tabList?.reset();
          return;
        }

        this.hidden = false;
        this.classList.remove('is-closing');
        document.body.classList.add('has-modal-open');
        this.activeElement = focusElement || document.activeElement;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.classList.add('is-open');
            this.tabList?.reset();
            theme.a11y?.trapFocus?.(this, this.focusElement);
          });
        });
      }

      hide() {
        if (!this.open && !this.classList.contains('is-closing')) return;

        const finish = () => {
          this.classList.remove('is-open', 'is-closing', 'is-dragging');
          this.hidden = true;
          if (this.panel) this.panel.style.transform = '';
          document.body.classList.remove('has-modal-open');
          theme.a11y?.removeTrapFocus?.(this.activeElement);
        };

        if (prefersReducedMotion()) {
          finish();
          return;
        }

        this.classList.remove('is-open', 'is-dragging');
        this.classList.add('is-closing');
        const panel = this.panel;
        const done = () => {
          panel?.removeEventListener('transitionend', onEnd);
          finish();
        };
        const onEnd = (event) => {
          if (event.target !== panel) return;
          if (event.propertyName !== 'transform') return;
          done();
        };
        panel?.addEventListener('transitionend', onEnd);
        setTimeout(done, 850);
      }
    }
  );
}

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    class CartRemoveButton extends window.BaseElementMixin(HTMLAnchorElement) {
      connectedCallback() {
        super.connectedCallback();

        this.on(this, 'click', (event) => {
          event.preventDefault();

          const cartItems = this.closest('cart-items');
          cartItems.updateQuantity(this.getAttribute('data-index'), 0);
        });
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('cart-warranty-upgrade')) {
  customElements.define(
    'cart-warranty-upgrade',
    class CartWarrantyUpgrade extends BaseElement {
      connectedCallback() {
        super.connectedCallback();
        this.onClickHandler = this.onClick.bind(this);
        this.addEventListener('click', this.onClickHandler);
        this.reconcileQuantity();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
        super.disconnectedCallback();
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get switchButton() {
        return this.querySelector('[role="switch"]');
      }

      async onClick(event) {
        const button = event.target.closest('[role="switch"]');
        if (button !== this.switchButton) return;
        if (this.hasAttribute('aria-busy')) return;

        this.setAttribute('aria-busy', 'true');
        button.disabled = true;
        this.setWarrantyControlsLoading(true);
        this.setTotalsLoading(true);
        const shouldAdd = button.getAttribute('aria-checked') !== 'true';
        if (shouldAdd) {
          this.updateWarranty(true);
          return;
        }

        button.setAttribute('aria-checked', 'false');
        this.setAttribute('aria-busy', 'true');
        const coveredLine = this.getAttribute('data-covered-line');
        const productRow = document.getElementById(`CartDrawer-Item-${coveredLine}`);
        productRow?.querySelector('.element-cart-item__warranty-badge')?.classList.add('is-leaving');
        await new Promise((resolve) => setTimeout(resolve, 180));
        this.updateWarranty(false);
      }

      syncFromCart(cart) {
        const coveredLine = this.getAttribute('data-covered-line');
        const variantId = Number(this.getAttribute('data-variant-id'));
        const coveredItem = cart.items?.find((item) => item.key === coveredLine);
        const warrantyItem = cart.items?.find((item) => {
          return item.variant_id === variantId && item.properties?.['_Covered line'] === coveredLine;
        });

        this.setAttribute('data-line-key', warrantyItem?.key || '');
        this.setAttribute('data-warranty-quantity', warrantyItem?.quantity || 0);
        this.switchButton?.setAttribute('aria-checked', warrantyItem ? 'true' : 'false');

        if (!coveredItem) {
          if (warrantyItem && !this.reconciling) {
            this.reconciling = true;
            this.updateWarranty(false, true);
          } else {
            this.remove();
          }
          return;
        }

        this.setAttribute('data-required-quantity', coveredItem.quantity);
        this.reconcileQuantity();
      }

      reconcileQuantity() {
        const lineKey = this.getAttribute('data-line-key');
        const requiredQuantity = Number(this.getAttribute('data-required-quantity'));
        const warrantyQuantity = Number(this.getAttribute('data-warranty-quantity'));
        if (lineKey && requiredQuantity !== warrantyQuantity && !this.reconciling) {
          this.reconciling = true;
          this.updateWarranty(true, true);
        }
      }

      async updateWarranty(shouldAdd, isReconcile = false) {
        const lineKey = this.getAttribute('data-line-key');
        const variantId = Number(this.getAttribute('data-variant-id'));
        const requiredQuantity = Number(this.getAttribute('data-required-quantity'));
        if ((shouldAdd && !variantId) || (!shouldAdd && !lineKey)) return;

        this.setAttribute('aria-busy', 'true');
        if (this.switchButton) this.switchButton.disabled = true;
        this.setWarrantyControlsLoading(true);
        this.setTotalsLoading(true);
        this.switchButton?.setAttribute('aria-checked', shouldAdd ? 'true' : 'false');
        const errorElement = this.querySelector('.element-cart-warranty__error');
        if (errorElement) {
          errorElement.hidden = true;
          errorElement.textContent = '';
        }

        const sections = [this.sectionId].filter(Boolean);
        const isNewWarranty = shouldAdd && !lineKey;
        const request = isNewWarranty
          ? {
              url: theme.routes.cart_add_url,
              body: {
                items: [{
                  id: variantId,
                  quantity: requiredQuantity,
                  properties: {
                    '_Warranty upgrade': 'true',
                    '_Covered line': this.getAttribute('data-covered-line'),
                    '_Covered product': this.getAttribute('data-covered-title')
                  }
                }],
                sections,
                sections_url: window.location.pathname
              }
            }
          : {
              url: theme.routes.cart_change_url,
              body: {
                id: lineKey,
                quantity: shouldAdd ? requiredQuantity : 0,
                sections,
                sections_url: window.location.pathname
              }
            };

        try {
          const response = await fetch(request.url, {
            ...theme.utils.fetchConfig(),
            body: JSON.stringify(request.body)
          });
          const result = await response.json();
          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Unable to update warranty');
          }

          let cart = result;
          if (isNewWarranty) {
            const cartResponse = await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'));
            cart = await cartResponse.json();
            if (!cartResponse.ok) throw new Error('Unable to refresh cart');
            cart.sections = result.sections;
          }

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-warranty',
            cart,
            coveredLine: this.getAttribute('data-covered-line')
          });
        } catch (error) {
          if (!isReconcile && this.switchButton) {
            this.switchButton.setAttribute('aria-checked', shouldAdd ? 'false' : 'true');
          }
          if (!isReconcile && !shouldAdd) {
            const coveredLine = this.getAttribute('data-covered-line');
            document
              .getElementById(`CartDrawer-Item-${coveredLine}`)
              ?.querySelector('.element-cart-item__warranty-badge')
              ?.classList.remove('is-leaving');
          }
          if (errorElement && !isReconcile) {
            errorElement.textContent = error.message;
            errorElement.hidden = false;
          }
          console.error(error);
        } finally {
          this.removeAttribute('aria-busy');
          if (this.switchButton) this.switchButton.disabled = false;
          this.setWarrantyControlsLoading(false);
          this.setTotalsLoading(false);
          this.reconciling = false;
        }
      }

      setWarrantyControlsLoading(isLoading) {
        const warrantyList = isLoading
          ? this.closest('[data-warranty-upgrades]')
          : this.loadingWarrantyList || this.closest('[data-warranty-upgrades]');
        if (!warrantyList) return;
        if (isLoading) this.loadingWarrantyList = warrantyList;

        warrantyList.classList.toggle('is-updating', isLoading);
        warrantyList.querySelectorAll('[role="switch"]').forEach((button) => {
          button.disabled = isLoading || button.dataset.warrantyAvailable === 'false';
        });

        if (isLoading) {
          warrantyList.setAttribute('aria-busy', 'true');
        } else {
          warrantyList.removeAttribute('aria-busy');
          this.loadingWarrantyList = null;
        }
      }

      setTotalsLoading(isLoading) {
        const totals = isLoading
          ? this.closest('[id^="MiniCart-"]')?.querySelector('.element-cart-total')
          : this.loadingTotals || this.closest('[id^="MiniCart-"]')?.querySelector('.element-cart-total');
        if (!totals) return;
        if (isLoading) this.loadingTotals = totals;
        totals.classList.toggle('is-updating', isLoading);
        if (isLoading) {
          totals.setAttribute('aria-busy', 'true');
        } else {
          totals.removeAttribute('aria-busy');
          this.loadingTotals = null;
        }
      }
    }
  );
}

if (!customElements.get('cart-warranty-orphan')) {
  customElements.define(
    'cart-warranty-orphan',
    class CartWarrantyOrphan extends HTMLElement {
      connectedCallback() {
        if (this.removing) return;
        this.removing = true;
        queueMicrotask(() => this.removeOrphan());
      }

      async removeOrphan() {
        const lineKey = this.getAttribute('data-line-key');
        const sectionId = this.getAttribute('data-section-id');
        if (!lineKey) return;

        try {
          const response = await fetch(theme.routes.cart_change_url, {
            ...theme.utils.fetchConfig(),
            body: JSON.stringify({
              id: lineKey,
              quantity: 0,
              sections: [sectionId].filter(Boolean),
              sections_url: window.location.pathname
            })
          });
          const cart = await response.json();
          if (!response.ok || cart.status) throw new Error(cart.description || 'Unable to remove orphaned warranty');
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-warranty',
            cart
          });
        } catch (error) {
          console.error(error);
          this.removing = false;
        }
      }
    }
  );
}

if (!customElements.get('cart-items')) {
  customElements.define(
    'cart-items',
    class CartItems extends BaseElement {

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      connectedCallback() {
        super.connectedCallback();

        this.on(this, 'change', theme.utils.debounce(this.onChange.bind(this), 300));
        const cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
        this.registerCleanup(cartUpdateUnsubscriber);
      }

      onChange(event) {
        this.validateQuantity(event);
      }

      async onCartUpdate(event) {
        if (event.cart.errors) {
          this.onCartError(event.cart.errors, event.target);
          return;
        }

        const sectionHtml = event.cart.sections?.[this.sectionId];
        if (!sectionHtml) {
          this.disableLoading(event.line);
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
          return;
        }
        const sectionToRender = new DOMParser().parseFromString(sectionHtml, 'text/html');

        const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);
        if (miniCart) {
          const updatedElement = sectionToRender.querySelector(`#MiniCart-${this.sectionId}`);
          if (!updatedElement) {
            this.disableLoading(event.line);
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
            return;
          }
          if (event.source === 'cart-discount') {
            miniCart.innerHTML = updatedElement.innerHTML;
            const cartDiscount = miniCart.querySelector(`#CartDiscount-${this.sectionId}`);
            if (cartDiscount) {
              cartDiscount.hidden = false;
              cartDiscount.setAttribute('open', 'immediate');
              cartDiscount.setAttribute('active', '');
            }
          } else {
            await syncMiniCartContents(miniCart, updatedElement, event.cart);
          }
        }

        const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
        if (mainCart) {
          const updatedElement = sectionToRender.querySelector(`#MainCart-${this.sectionId}`);
          if (updatedElement) {
            if (event.source === 'cart-discount') {
              const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
              if (cartDiscount) {
                cartDiscount.hidden = false;
                cartDiscount.setAttribute('open', '');
                cartDiscount.setAttribute('aria-expanded', 'true');
              }
            }
            
            const scrollTop = window.scrollY;
            mainCart.innerHTML = updatedElement.innerHTML;

            if (event.source === 'cart-discount') {
              requestIdleCallback(() => {
                window.scrollTo({
                  top: scrollTop,
                  behavior: 'instant'
                });
              });
            }
          }
          else {
            mainCart.closest('.cart').classList.add('is-empty');
            mainCart.remove();
          }
        }

        const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
        if (event.source === 'cart-warranty' && miniCart) {
          const updatedWarranty = Array.from(
            miniCart.querySelectorAll('cart-warranty-upgrade')
          ).find((upgrade) => upgrade.dataset.coveredLine === event.coveredLine);
          const updatedSwitch = updatedWarranty?.querySelector('[role="switch"]');
          updatedSwitch?.focus({ preventScroll: true });
        }
        else if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
          theme.a11y.trapFocus(mainCart || miniCart, lineItem.querySelector(`[name="${event.name}"]`));
        }
        else if (event.cart.item_count === 0) {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('[data-empty-cart-add], [data-cart-close], a'))
            : theme.a11y.trapFocus(document.querySelector('.empty-state'), document.querySelector('.empty-state__link'));
        }
        else {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('.horizontal-product__title'))
            : theme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart__item-title'));
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: event.cart
          }
        }));
      }

      onCartError(errors, target) {
        if (target) {
          // this.updateQuantity(target.getAttribute('data-index'), target.defaultValue, document.activeElement.getAttribute('name'), target);
          this.disableLoading(target.getAttribute('data-index'));
          this.setValidity(target, errors);
          return;
        }
        else {
          window.location.href = theme.routes.cart_url;
        }
      }

      updateQuantity(line, quantity, name, target) {
        this.enableLoading(line);

        this.updateAbortController?.abort();
        this.updateAbortController = new AbortController();

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        if (this.sectionId && !sectionsToBundle.includes(this.sectionId)) {
          sectionsToBundle.push(this.sectionId);
        }

        const body = JSON.stringify({
          id: line,
          quantity,
          sections: sectionsToBundle,
          sections_url: window.location.pathname
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body }, signal: this.updateAbortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
          .finally(() => {
            this.disableLoading(line);
          });
      }

      enableLoading(line) {
        this.classList.add('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = false;

        const totals = this.closest('[id^="MiniCart-"]')?.querySelector('.element-cart-total');
        totals?.classList.add('is-updating');
        totals?.setAttribute('aria-busy', 'true');
      }

      disableLoading(line) {
        this.classList.remove('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = true;

        const totals = this.closest('[id^="MiniCart-"]')?.querySelector('.element-cart-total');
        totals?.classList.remove('is-updating');
        totals?.removeAttribute('aria-busy');
      }

      setValidity(target, message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();
      }

      confirmRemoval(target) {
        const drawer = this.closest('#CartDrawer');
        const label = target.dataset.removeLabel || 'this item';
        const [product, finish] = label.split(' — ');
        const quantity = Number(target.defaultValue) || 1;
        const dialog = document.createElement('dialog');
        dialog.className = 'element-cart-remove-dialog';
        dialog.setAttribute('aria-labelledby', 'CartRemoveTitle');
        dialog.setAttribute('aria-describedby', 'CartRemoveMessage');
        dialog.innerHTML = `<button type="button" class="element-cart-remove-dialog__close" data-keep aria-label="Keep item and close">×</button>
          <div class="element-cart-remove-dialog__image"></div>
          <small>YOUR SELECTION</small>
          <h2 id="CartRemoveTitle" tabindex="-1" autofocus></h2>
          <p id="CartRemoveMessage"></p>
          <div class="element-cart-remove-dialog__actions"><button type="button" data-keep>Keep in cart</button><button type="button" data-remove>Remove</button></div>`;
        dialog.querySelector('h2').textContent = finish && finish !== 'Default Title' ? `Remove your ${finish} finish?` : `Remove ${product}?`;
        dialog.querySelector('p').textContent = `${quantity > 1 ? `Your ${quantity} ${product} units` : `Your ${product}`}${finish && finish !== 'Default Title' ? ` in ${finish}` : ''} will be removed from your cart. You can add ${quantity > 1 ? 'them' : 'it'} back at any time.`;
        const originalImage = target.closest('.horizontal-product')?.querySelector('.horizontal-product__media img');
        if (originalImage) {
          const image = document.createElement('img');
          image.src = originalImage.currentSrc || originalImage.src;
          image.alt = originalImage.alt || label;
          dialog.querySelector('.element-cart-remove-dialog__image').append(image);
        } else dialog.querySelector('.element-cart-remove-dialog__image').hidden = true;
        drawer.append(dialog);
        return new Promise(resolve => {
          let remove = false;
          dialog.addEventListener('click', event => {
            if (event.target.closest('[data-remove]')) { remove = true; dialog.close(); }
            else if (event.target.closest('[data-keep]') || event.target === dialog) dialog.close();
          });
          dialog.addEventListener('close', () => {
            dialog.remove();
            if (!remove) target.closest('quantity-input')?.querySelector('button[name="minus"]')?.focus({preventScroll:true});
            resolve(remove);
          }, {once:true});
          dialog.showModal();
          dialog.querySelector('h2').focus({preventScroll:true});
        });
      }

      async validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        let message = '';

        if (inputValue === 0 && this.closest('#CartDrawer')) {
          target.value = target.defaultValue;
          target.closest('quantity-input')?.validateQtyRules?.();
          if (this.removalPending) return;
          this.removalPending = true;
          let remove;
          try { remove = await this.confirmRemoval(target); }
          finally { this.removalPending = false; }
          if (!remove || !target.isConnected) return;
          target.setCustomValidity('');
          this.updateQuantity(index, 0, target.name, target);
          return;
        }

        if (inputValue < parseInt(target.getAttribute('data-min'))) {
          message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
        }
        else if (inputValue > parseInt(target.max)) {
          message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
        }
        else if (inputValue % parseInt(target.step) !== 0) {
          message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
        }

        if (message) {
          this.setValidity(target, message);
        }
        else {
          target.setCustomValidity('');
          target.reportValidity();
          this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        }
      }
    }
  );
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        this.on(this, 'change', theme.utils.debounce(this.onChange.bind(this), 300));
      }

      onChange(event) {
        const body = JSON.stringify({ note: event.target.value });
        // Fire-and-forget attribute update — the response is ignored, so there is
        // no stale-response risk and nothing to abort.
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } });
      }
    }
  );
}

if (!customElements.get('main-cart')) {
  customElements.define(
    'main-cart',
    class MainCart extends BaseElement {
      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      connectedCallback() {
        super.connectedCallback();

        this.on(document, 'cart:bundled-sections', this.onPrepareBundledSections.bind(this));
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }
    }
  );
}

if (!customElements.get('country-province')) {
  customElements.define(
    'country-province',
    class CountryProvince extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        this.provinceElement = this.querySelector('[name="address[province]"]');
        this.countryElement = this.querySelector('[name="address[country]"]');
        this.on(this.countryElement, 'change', this.handleCountryChange.bind(this));

        if (this.getAttribute('country') !== '') {
          this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
          this.countryElement.dispatchEvent(new Event('change'));
        }
        else {
          this.handleCountryChange();
        }
      }

      handleCountryChange() {
        const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute('data-provinces'));
        this.provinceElement.parentElement.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          return;
        }

        this.provinceElement.innerHTML = '';

        provinces.forEach((data) => {
          const selected = data[1] === this.getAttribute('data-province');
          this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
        });
      }
    }
  );
}

if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends window.BaseElementMixin(HTMLFormElement) {
      constructor() {
        super();

        this.onSubmitHandler = this.onSubmit.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();

        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.on(this.submitButton, 'click', this.onSubmitHandler);
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.ratesAbortController?.abort();
      }

      onSubmit(event) {
        event.preventDefault();

        this.ratesAbortController?.abort();
        this.ratesAbortController = new AbortController();

        const zip = this.querySelector('[name="address[zip]"]').value,
          country = this.querySelector('[name="address[country]"]').value,
          province = this.querySelector('[name="address[province]"]').value;

        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          shipping_address: { zip, country, province }
        });
        let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

        // remove double `/` in case shop might have /en or language in URL
        sectionUrl = sectionUrl.replace('//', '/');

        fetch(sectionUrl, { ...theme.utils.fetchConfig('javascript'), ...{ body }, signal: this.ratesAbortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.shipping_rates) {
              this.formatShippingRates(parsedState.shipping_rates);
            }
            else {
              this.formatError(parsedState);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
          .finally(() => {
            this.resultsElement.hidden = false;
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      formatError(errors) {
        const shippingRatesList = Object.keys(errors).map((errorKey) => {
          return `<li>${errors[errorKey]}</li>`;
        });
        this.resultsElement.classList.remove('alert--success');
        this.resultsElement.classList.add('alert--error');
        this.resultsElement.lastElementChild.innerHTML = `
          <p>${theme.shippingCalculatorStrings.error}</p>
          <ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>
        `;
      }

      formatShippingRates(shippingRates) {
        const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
          return `<li>${presentment_name}: ${currency} ${price}</li>`;
        });
        if (shippingRates.length) {
          this.resultsElement.classList.remove('alert--error');
          this.resultsElement.classList.add('alert--success');
        }
        else {
          this.resultsElement.classList.remove('alert--success');
          this.resultsElement.classList.add('alert--error');
        }
        this.resultsElement.lastElementChild.innerHTML = `
          <p>${shippingRates.length === 0 ? theme.shippingCalculatorStrings.notFound : shippingRates.length === 1 ? theme.shippingCalculatorStrings.oneResult : theme.shippingCalculatorStrings.multipleResults}</p>
          ${shippingRatesList === '' ? '' : `<ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>`}
        `;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends window.BaseElementMixin(HTMLFormElement) {
      constructor() {
        super();

        this.onApplyDiscount = this.applyDiscount.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      connectedCallback() {
        super.connectedCallback();

        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.on(this.submitButton, 'click', this.onApplyDiscount);
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.discountAbortController?.abort();
      }

      applyDiscount(event) {
        event.preventDefault();

        const discountCode = this.querySelector('[name="discount"]');
        if (!(discountCode instanceof HTMLInputElement) || typeof this.getAttribute('data-section-id') !== 'string') return;

        this.discountAbortController?.abort();
        this.discountAbortController = new AbortController();

        const discountCodeValue = discountCode.value.trim();
        if (discountCodeValue === '') return;

        const existingDiscounts = this.existingDiscounts();
        if (existingDiscounts.includes(discountCodeValue)) return;

        this.setDiscountError('');
        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.discountAbortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (
              parsedState.discount_codes.find((discount) => {
                return discount.code === discountCodeValue && discount.applicable === false;
              })
            ) {
              discountCode.value = '';
              this.setDiscountError(theme.discountStrings.error);
              return;
            }

            const newHtml = parsedState.sections[this.sectionId];
            const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
            const section = parsedHtml.getElementById(`shopify-section-${this.sectionId}`);
            if (section) {
              const discountCodes = section?.querySelectorAll('button[is="discount-remove"]') || [];
              const codes = Array.from(discountCodes)
                .map((element) => (element instanceof HTMLButtonElement ? element.getAttribute('data-discount') : null))
                .filter(Boolean);

              if (
                codes.length === existingDiscounts.length &&
                codes.every((code) => existingDiscounts.includes(code)) &&
                parsedState.discount_codes.find((discount) => {
                  return discount.code === discountCodeValue && discount.applicable === true;
                })
              ) {
                discountCode.value = '';
                this.setDiscountError(theme.discountStrings.shippingError);
                return;
              }
            }

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      removeDiscount(event) {
        if ((event instanceof KeyboardEvent && event.key !== 'Enter') || !(event instanceof MouseEvent)) {
          return;
        }

        const discountCode = event.target.getAttribute('data-discount');
        if (!discountCode) return;

        const existingDiscounts = this.existingDiscounts();
        const index = existingDiscounts.indexOf(discountCode);
        if (index === -1) return;

        existingDiscounts.splice(index, 1);

        this.discountAbortController?.abort();
        this.discountAbortController = new AbortController();

        this.setDiscountError('');
        event.target.setAttribute('loading', '');

        const body = JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.discountAbortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
          .finally(() => {
            event.target.removeAttribute('loading');
          });
      }

      existingDiscounts() {
        const discountCodes = [];
        const discountPills = this.querySelectorAll('button[is="discount-remove"]');
        for (const pill of discountPills) {
          if (pill.hasAttribute('data-discount')) {
            discountCodes.push(pill.getAttribute('data-discount'));
          }
        }
        return discountCodes;
      }

      setDiscountError(error) {
        this.resultsElement.lastElementChild.textContent = error;
        this.resultsElement.hidden = error.length === 0;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('discount-remove')) {
  customElements.define(
    'discount-remove',
    class DiscountRemove extends window.BaseElementMixin(HTMLButtonElement) {
      connectedCallback() {
        if (typeof super.connectedCallback === 'function') super.connectedCallback();
        if (typeof this.on === 'function') {
          this.on(this, 'click', this.onClick);
        } else {
          this.addEventListener('click', this.onClick);
        }
      }

      onClick(event) {
        const form = this.closest('form[is="cart-discount"]') || document.querySelector('form[is="cart-discount"]');

        if (form) {
          event.preventDefault();
          form.removeDiscount(event);
        }
      }
    }, { extends: 'button' }
  );
}
